import React, { useState } from 'react';
import { Button } from './ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';
import { Loader2, ChevronDown, ChevronUp, Download, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { assetsLiabilitiesAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

/**
 * A&L Entries tab: itemizes every document behind every balance-sheet
 * number, one collapsible per section, each with its own on-line
 * reconciliation footer. Every section lazy-loads on first expand -- the
 * pipeline section alone can be 1000+ rows, no reason to fetch any of
 * these before the operator actually opens one.
 */

const SECTIONS = [
  {
    key: 'cash', label: 'Cash', informational: false,
    columns: [
      { key: 'entity_name', label: 'Entity' },
      { key: 'account_name', label: 'Account' },
      { key: 'bank_name', label: 'Bank' },
      { key: 'amount', label: 'Balance', numeric: true },
      { key: 'as_of', label: 'As of' },
      { key: 'source', label: 'Source' },
    ],
  },
  {
    key: 'unpaid_invoices', label: 'Unpaid invoices', informational: false,
    columns: [
      { key: 'entity_name', label: 'Entity' },
      { key: 'invoice_number', label: 'Invoice #' },
      { key: 'counterparty', label: 'Counterparty' },
      { key: 'issue_date', label: 'Issue date' },
      { key: 'due_date', label: 'Due date' },
      { key: 'amount', label: 'Amount', numeric: true },
      { key: 'payment_status', label: 'Payment status' },
      { key: 'review_status', label: 'Review status' },
    ],
  },
  {
    key: 'unpaid_mgmt_statements', label: 'Unpaid mgmt statements', informational: false,
    columns: [
      { key: 'entity_name', label: 'Entity' },
      { key: 'period', label: 'Period' },
      { key: 'property', label: 'Property' },
      { key: 'amount', label: 'Amount', numeric: true },
      { key: 'reconciliation_status', label: 'Status' },
    ],
  },
  {
    key: 'unpaid_bros_invoices', label: 'Unpaid BR OS invoices', informational: false,
    columns: [
      { key: 'invoice_number', label: 'Invoice #' },
      { key: 'client', label: 'Client' },
      { key: 'issue_date', label: 'Issue date' },
      { key: 'amount', label: 'Amount', numeric: true },
      { key: 'status', label: 'Status' },
    ],
  },
  {
    key: 'unpaid_bookings', label: 'Unpaid bookings', informational: false,
    columns: [
      { key: 'entity_name', label: 'Entity' },
      { key: 'platform', label: 'Platform' },
      { key: 'property', label: 'Property' },
      { key: 'guest', label: 'Guest' },
      { key: 'check_in', label: 'Check-in' },
      { key: 'check_out', label: 'Check-out' },
      { key: 'amount', label: 'Amount', numeric: true },
    ],
  },
  {
    key: 'intercompany', label: 'Intercompany receivables/loans', informational: false,
    columns: [
      { key: 'counterparty_name', label: 'Counterparty' },
      { key: 'amount', label: 'Amount', numeric: true },
      { key: 'direction', label: 'Direction' },
    ],
  },
  {
    key: 'eliminated_overlap', label: 'Eliminated overlap', informational: true,
    columns: [
      { key: 'entity_name', label: 'Entity' },
      { key: 'bros_invoice_number', label: 'BR OS invoice #' },
      { key: 'duplicate_of', label: 'Duplicate of' },
      { key: 'flavor', label: 'Flavor' },
      { key: 'amount', label: 'Amount', numeric: true },
    ],
  },
  {
    key: 'intra_group_eliminated', label: 'Intra-group eliminated', informational: true,
    columns: [
      { key: 'counterparty_name', label: 'Counterparty' },
      { key: 'amount', label: 'Amount', numeric: true },
      { key: 'direction', label: 'Direction' },
    ],
  },
  {
    key: 'forward_bookings', label: 'Forward bookings', informational: true,
    columns: [
      { key: 'description', label: 'Description' },
      { key: 'amount', label: 'Amount', numeric: true },
    ],
  },
  {
    key: 'managed_bookings_pipeline', label: 'Managed bookings awaiting statement', informational: true,
    columns: [
      { key: 'entity_name', label: 'Entity' },
      { key: 'platform', label: 'Platform' },
      { key: 'property', label: 'Property' },
      { key: 'guest', label: 'Guest' },
      { key: 'check_in', label: 'Check-in' },
      { key: 'check_out', label: 'Check-out' },
      { key: 'amount', label: 'Amount', numeric: true },
    ],
  },
  {
    key: 'scope_excluded_tail', label: 'Pre-2026 scope-excluded tail', informational: true,
    columns: [
      { key: 'entity_name', label: 'Entity' },
      { key: 'platform', label: 'Platform' },
      { key: 'property', label: 'Property' },
      { key: 'check_in', label: 'Check-in' },
      { key: 'check_out', label: 'Check-out' },
      { key: 'amount', label: 'Amount', numeric: true },
    ],
  },
];

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function ReconciliationFooter({ reconciliation }) {
  if (!reconciliation) return null;
  if (reconciliation.reconciles) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400 mt-2" data-testid="entries-reconciliation-ok">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Reconciles to the balance sheet
      </div>
    );
  }
  const mismatches = Object.entries(reconciliation.by_currency || {}).filter(([, v]) => !v.reconciles);
  return (
    <div
      className="mt-2 rounded-md border border-red-300 bg-red-50 dark:bg-red-950/30 p-2 text-xs text-red-700 dark:text-red-400"
      data-testid="entries-reconciliation-discrepancy"
    >
      <div className="flex items-center gap-1.5 font-semibold">
        <AlertTriangle className="h-3.5 w-3.5" />
        Discrepancy -- does not reconcile
      </div>
      {mismatches.map(([cur, v]) => (
        <div key={cur} className="mt-1">
          {cur}: listed {v.listed_sum_native} vs balance sheet {v.balance_sheet_native}
        </div>
      ))}
    </div>
  );
}

function EntriesSection({ section, scope, entityId, displayCurrency }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [downloading, setDownloading] = useState(false);

  const load = async (p) => {
    setLoading(true);
    try {
      const resp = await assetsLiabilitiesAPI.getEntriesSection(section.key, scope, entityId, displayCurrency, p, 100);
      setData(resp.data);
      setPage(p);
      setLoaded(true);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !loaded) {
      load(1);
    }
  };

  const handleDownloadCsv = async () => {
    setDownloading(true);
    try {
      const resp = await assetsLiabilitiesAPI.getEntriesSectionCsv(section.key, scope, entityId, displayCurrency);
      downloadBlob(new Blob([resp.data], { type: 'text/csv' }), `al-entries-${section.key}.csv`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="border rounded-md" data-testid={`entries-section-${section.key}`}>
      <button
        type="button"
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium"
        onClick={handleToggle}
        data-testid={`entries-section-toggle-${section.key}`}
      >
        <span className="flex items-center gap-1.5">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          {section.label}
          {section.informational && (
            <span className="text-[10px] text-muted-foreground uppercase tracking-wide ml-1">(outside totals)</span>
          )}
        </span>
        {loaded && data && (
          <span className="text-xs text-muted-foreground">{data.total_count} entries</span>
        )}
      </button>
      {expanded && (
        <div className="px-3 pb-3" data-testid={`entries-section-body-${section.key}`}>
          {loading && !data ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : data ? (
            <>
              <div className="flex justify-end mb-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadCsv}
                  disabled={downloading}
                  data-testid={`entries-csv-${section.key}`}
                >
                  {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Download className="h-3.5 w-3.5 mr-1" />}
                  Download CSV
                </Button>
              </div>
              {data.rows.length === 0 ? (
                <p className="text-xs text-muted-foreground">No entries</p>
              ) : (
                <Table className="text-xs">
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      {section.columns.map((c) => (
                        <TableHead key={c.key} className={`h-auto px-2 py-1 font-medium ${c.numeric ? 'text-right' : 'text-left'}`}>
                          {c.label}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.rows.map((row, i) => (
                      <TableRow key={i}>
                        {section.columns.map((c) => (
                          <TableCell key={c.key} className={`px-2 py-1 ${c.numeric ? 'text-right font-mono' : ''}`}>
                            {c.numeric
                              ? (row[c.key] != null ? formatCurrency(row[c.key], row.currency) : '')
                              : (row[c.key] ?? '')}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {data.total_pages > 1 && (
                <div className="flex items-center justify-between mt-2 text-xs" data-testid={`entries-pager-${section.key}`}>
                  <Button variant="ghost" size="sm" disabled={page <= 1 || loading} onClick={() => load(page - 1)}>
                    Previous
                  </Button>
                  <span className="text-muted-foreground">{page} / {data.total_pages}</span>
                  <Button variant="ghost" size="sm" disabled={page >= data.total_pages || loading} onClick={() => load(page + 1)}>
                    Next
                  </Button>
                </div>
              )}
              <ReconciliationFooter reconciliation={data.reconciliation} />
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function AlEntriesTab({ scope, entityId, displayCurrency }) {
  return (
    <div className="space-y-2" data-testid="al-entries-tab-content">
      {SECTIONS.map((section) => (
        <EntriesSection
          key={section.key}
          section={section}
          scope={scope}
          entityId={entityId}
          displayCurrency={displayCurrency}
        />
      ))}
    </div>
  );
}

export { SECTIONS as AL_ENTRIES_SECTIONS };
